import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useGet } from '../hooks';
import { StoreBasicData, ChannelData, StoreData } from '@/types/store';
import {
  BackendBasicRestaurantResponse,
  BackendChannelInfoResponse,
} from '@/types/channels';
import {
  normalizeBasicStoreResponse,
  normalizeChannelInfoResponse,
} from '@/utils/storeNormalizer';
import { useUserAccess } from './UserAccessContext';

const CACHE_TIMESTAMP_KEY = 'channelDataCacheTimestamp';
const PAGE_RELOAD_CACHE_TTL = 60 * 1000;

interface StoreContextState {
  basicData: StoreBasicData | null;
  basicLoading: boolean;
  basicError: string | null;

  channelData: ChannelData | null;
  channelLoading: boolean;
  channelError: string | null;
  channelDataFetched: boolean;

  data: StoreData | null;
  loading: boolean;
  error: string | null;

  restaurantId: number | null;
  restaurantIdLoading: boolean;
  restaurantIdError: string | null;

  fetchChannelInfo: (forceRefresh?: boolean) => void;
  refetchBasic: () => void;
  refetchChannels: () => void;
  refetch: () => void;
  refetchRestaurantId: () => void;
}

interface StoreProviderProps {
  children: ReactNode;
}

const StoreContext = createContext<StoreContextState | undefined>(undefined);

export const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
  const { selectedStore, loading: userAccessLoading } = useUserAccess();
  const restaurantId = selectedStore?.storeId ?? null;

  const [basicData, setBasicData] = useState<StoreBasicData | null>(null);
  const [channelData, setChannelData] = useState<ChannelData | null>(null);
  const [channelDataFetched, setChannelDataFetched] = useState(false);

  const pageReloadHandledRef = useRef(false);

  const isPageReload = useMemo(() => {
    if (typeof window !== 'undefined' && window.performance) {
      const navEntries = performance.getEntriesByType(
        'navigation'
      ) as PerformanceNavigationTiming[];
      if (navEntries.length > 0) {
        return navEntries[0].type === 'reload';
      }
    }
    return false;
  }, []);

  const channelLoadingRef = useRef(false);

  const setCacheTimestamp = useCallback(() => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    }
  }, []);

  const getCacheTimestamp = useCallback((): number | null => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const timestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);
      return timestamp ? parseInt(timestamp, 10) : null;
    }
    return null;
  }, []);

  const isCacheExpired = useCallback((): boolean => {
    const timestamp = getCacheTimestamp();
    if (!timestamp) return true;
    return Date.now() - timestamp >= PAGE_RELOAD_CACHE_TTL;
  }, [getCacheTimestamp]);

  // Fetch basic store info from /stores/{id}
  const {
    data: backendBasicData,
    loading: basicLoading,
    error: basicError,
    refetch: refetchBasicStore,
  } = useGet<BackendBasicRestaurantResponse>('', {}, false, true);

  // Fetch channel info from /stores/{id}/info
  const {
    data: backendChannelData,
    loading: channelLoading,
    error: channelError,
    refetch: refetchChannelStore,
  } = useGet<BackendChannelInfoResponse>('', {}, false, true);

  // Reset state when store changes
  useEffect(() => {
    setBasicData(null);
    setChannelData(null);
    setChannelDataFetched(false);
    pageReloadHandledRef.current = false;
  }, [restaurantId]);

  // Fetch basic store info when restaurant ID is available
  useEffect(() => {
    if (restaurantId !== null) {
      refetchBasicStore(
        process.env.REACT_APP_API_DOMAIN + `stores/${restaurantId}`
      );
    }
  }, [restaurantId, refetchBasicStore]);

  // Normalize basic store data
  useEffect(() => {
    if (backendBasicData && backendBasicData.id === restaurantId) {
      setBasicData(normalizeBasicStoreResponse(backendBasicData));
    }
  }, [backendBasicData, restaurantId]);

  useEffect(() => {
    channelLoadingRef.current = channelLoading;
  }, [channelLoading]);

  // Normalize channel data when received
  useEffect(() => {
    if (backendChannelData && backendChannelData.id === restaurantId) {
      setChannelData(normalizeChannelInfoResponse(backendChannelData));
      setChannelDataFetched(true);
    }
  }, [backendChannelData, restaurantId]);

  const fetchChannelInfo = useCallback(
    (forceRefresh = false) => {
      if (restaurantId === null || channelLoadingRef.current) {
        return;
      }

      let headers: Record<string, string> | undefined;

      if (forceRefresh) {
        headers = { 'X-Force-Refresh': 'true' };
      } else if (isPageReload && !pageReloadHandledRef.current) {
        pageReloadHandledRef.current = true;
        const cacheTimestamp = getCacheTimestamp();
        if (cacheTimestamp === null) {
          setCacheTimestamp();
        } else if (isCacheExpired()) {
          headers = { 'X-Force-Refresh': 'true' };
          setCacheTimestamp();
        }
      }

      refetchChannelStore(
        process.env.REACT_APP_API_DOMAIN + `stores/${restaurantId}/info`,
        undefined,
        headers
      );
    },
    [
      restaurantId,
      refetchChannelStore,
      isPageReload,
      getCacheTimestamp,
      isCacheExpired,
      setCacheTimestamp,
    ]
  );

  const refetchBasic = useCallback(() => {
    if (restaurantId !== null) {
      refetchBasicStore(
        process.env.REACT_APP_API_DOMAIN + `stores/${restaurantId}`
      );
    }
  }, [restaurantId, refetchBasicStore]);

  const refetchChannels = useCallback(() => {
    if (restaurantId !== null) {
      setChannelDataFetched(false);
      refetchChannelStore(
        process.env.REACT_APP_API_DOMAIN + `stores/${restaurantId}/info`,
        undefined,
        { 'X-Force-Refresh': 'true' }
      );
    }
  }, [restaurantId, refetchChannelStore]);

  const refetch = useCallback(() => {
    refetchBasic();
    if (channelDataFetched) {
      refetchChannels();
    }
  }, [refetchBasic, refetchChannels, channelDataFetched]);

  const combinedData: StoreData | null = basicData
    ? {
        ...basicData,
        channels: channelData?.channels || {},
        channelGroups: channelData?.channelGroups || {},
      }
    : null;

  const contextValue: StoreContextState = {
    basicData,
    basicLoading: userAccessLoading || basicLoading,
    basicError: basicError,
    channelData,
    channelLoading,
    channelError,
    channelDataFetched,
    data: combinedData,
    loading: userAccessLoading || basicLoading,
    error: basicError,
    restaurantId,
    restaurantIdLoading: userAccessLoading,
    restaurantIdError: null,
    fetchChannelInfo,
    refetchBasic,
    refetchChannels,
    refetch,
    refetchRestaurantId: refetch,
  };

  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = (): StoreContextState => {
  const context = useContext(StoreContext);

  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }

  return context;
};

export { StoreContext };
